import { Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { getListingTransactionMode } from 'app/lib/listing';
import { ListingSubtype, ListingType, TransactionMode, type Listing } from 'app/types/listing';
import { WhiteLabel } from 'app/types/white-labels';
import { isWhiteLabel } from 'app/utils/helpers';
import { useTranslation } from 'react-i18next';
import { DONATION_BADGE_BACKGROUND_COLOR } from './listing-card-metadata.constants';
import { getDateTag, isOfferListingNew } from './listing-card-metadata.utils';
import { StatusPerishable } from './status-perishable';

export function ListingCardTimeTag({ isNew, listing }: { isNew?: boolean; listing: Listing; compact?: boolean }) {
  const { t } = useTranslation('all-listings');
  const tag = getDateTag(listing);
  const resolvedIsNew = isNew ?? isOfferListingNew(listing.createdAt);
  if (!tag && !resolvedIsNew) return null;

  return (
    <Row
      alignItems="center"
      backgroundColor={
        tag?.urgent ? 'icon.error' : resolvedIsNew ? 'surface.brand.default' : 'surface.inverse.tertiary-neutral'
      }
      borderRadius="radius.sm"
      flexShrink={1}
      maxWidth="100%"
      width="auto"
      paddingHorizontal="spacing.general.x2"
      paddingVertical="spacing.general.x1"
    >
      <Text
        color={tag?.urgent || resolvedIsNew ? 'white' : 'text.primary'}
        flexShrink={1}
        numberOfLines={2}
        variant="bodyXSmall"
      >
        {resolvedIsNew && !tag?.urgent ? (
          <Text color="white" variant="bodyXSmallStrong">
            {t('new')}{' '}
          </Text>
        ) : null}
        {tag ? t(tag.key, { ...tag.values }) : null}
      </Text>
    </Row>
  );
}

export function ListingCardModeBadge({ listing }: { listing: Listing }) {
  const { t } = useTranslation('all-listings');

  if (!isWhiteLabel(WhiteLabel.HauteGaspesie)) return null;

  const mode = getListingTransactionMode(listing);
  const backgroundColor =
    mode === TransactionMode.sell
      ? '#F7E9AB'
      : mode === TransactionMode.give
        ? DONATION_BADGE_BACKGROUND_COLOR
        : '#9B72A8';

  return (
    <Row
      alignItems="center"
      borderRadius="radius.lg"
      paddingHorizontal="spacing.general.x2"
      paddingVertical="spacing.general.x1"
      style={{ backgroundColor }}
    >
      <Text color={mode === TransactionMode.lend ? 'white' : 'text.primary'} fontSize={12} fontWeight="700">
        {t(`${listing.type === ListingType.request ? 'requestMode' : 'mode'}.${mode}`)}
      </Text>
    </Row>
  );
}

export function ListingCardPerishableIcon({ listing }: { listing: Listing }) {
  if (listing.subtype !== ListingSubtype.expirable) return null;

  return <StatusPerishable withText={false} />;
}

export function ListingCardPrice({ listing }: { listing: Listing }) {
  if (getListingTransactionMode(listing) !== TransactionMode.sell || !listing.price) return null;
  return <Text fontSize={16}>{listing.price}</Text>;
}

export { DONATION_BADGE_BACKGROUND_COLOR } from './listing-card-metadata.constants';
export { getDateTag, isOfferListingNew } from './listing-card-metadata.utils';
